import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getMember, invalidateMemberCache, type MemberResponse } from '@/api/auth';
import { getToken, getUser, isLoggedIn, setUser, type AuthUser } from '@/utils/auth';

/** 无需拉取账号信息的公开路由 */
const PUBLIC_PATHS = new Set(['/', '/login', '/404']);

interface MemberContextValue {
  member: AuthUser | null;
  loading: boolean;
  flowsCount: number;
  purchasePlansCount: number;
  refresh: (options?: { force?: boolean }) => Promise<MemberResponse | null>;
  clear: () => void;
}

const MemberContext = createContext<MemberContextValue | null>(null);

function syncUserFromMember(res: MemberResponse): AuthUser | null {
  if (!res.data) return null;
  const token = getToken() || undefined;
  const nextUser: AuthUser = {
    ...res.data,
    token,
    is_canedit: res.data.is_canedit ?? res.is_canedit,
    is_divide: res.data.is_divide ?? res.is_divide,
    is_purchase_plan_manager:
      res.data.is_purchase_plan_manager ?? res.is_purchase_plan_manager,
  };
  setUser(nextUser);
  return nextUser;
}

export function MemberProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [member, setMember] = useState<AuthUser | null>(() => getUser());
  const [loading, setLoading] = useState(true);
  const [flowsCount, setFlowsCount] = useState(0);
  const [purchasePlansCount, setPurchasePlansCount] = useState(0);

  const refresh = useCallback(async (options?: { force?: boolean }) => {
    try {
      const res = await getMember(options);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) return res;

      const nextUser = syncUserFromMember(res);
      if (nextUser) setMember(nextUser);

      const flows = Number(res.flows_count ?? 0);
      const plans = Number(res.purchase_plans_count ?? 0);
      setFlowsCount(Number.isFinite(flows) && flows > 0 ? flows : 0);
      setPurchasePlansCount(Number.isFinite(plans) && plans > 0 ? plans : 0);
      return res;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    invalidateMemberCache();
    setMember(null);
    setFlowsCount(0);
    setPurchasePlansCount(0);
    setLoading(false);
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    // 登录页 / 根路径跳转 / 未登录：不请求 member，避免后端 498「未提供认证令牌」弹窗
    if (PUBLIC_PATHS.has(pathname) || !isLoggedIn() || !getToken()) {
      if (pathname === '/login' || !getToken()) {
        clear();
      } else {
        setLoading(false);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await refresh({ force: false });
      if (cancelled) return;
      if (!res) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, refresh, clear]);

  const value = useMemo(
    () => ({
      member,
      loading,
      flowsCount,
      purchasePlansCount,
      refresh,
      clear,
    }),
    [member, loading, flowsCount, purchasePlansCount, refresh, clear],
  );

  return <MemberContext.Provider value={value}>{children}</MemberContext.Provider>;
}

export function useMember() {
  const ctx = useContext(MemberContext);
  if (!ctx) {
    throw new Error('useMember must be used within MemberProvider');
  }
  return ctx;
}
